import { useEffect, useState } from 'react';

/**
 * Phones and tablets: a finger is the only pointer and nothing hovers.
 *
 * An iPad with a trackpad answers `fine` and keeps the desktop player, which is
 * the right call - it has a pointer that can hover over a 38 px button. Asked
 * of the media query rather than the user agent, so a laptop with a touch
 * screen and a mouse stays a laptop.
 */
const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

export const isTouchUI = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.(TOUCH_QUERY).matches);

export const useTouchUI = () => {
  const [touch, setTouch] = useState(isTouchUI);
  useEffect(() => {
    const query = window.matchMedia?.(TOUCH_QUERY);
    if (!query) return undefined;
    const onChange = () => setTouch(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return touch;
};

/** iPhone, iPod, and the iPad that has called itself a Mac since iPadOS 13. */
export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/** Opened from the home screen rather than in a browser tab. */
export const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

/**
 * A short tick under the finger where the platform has one. Android Chrome
 * does; iOS Safari has no vibration API at all, so there this is silent.
 */
export const haptic = (ms = 8) => {
  try {
    navigator.vibrate?.(ms);
  } catch {
    // Some browsers throw when called without a recent gesture; a missing tick
    // is not worth an error
  }
};
